import React, { useState, useEffect } from 'react';
import { useToken } from '../App';
import { API_ENDPOINTS } from '../config/api';
import { useCart } from '../components/CartContext';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { useGuest } from '../App';
import { MdRestaurantMenu, MdSearch } from 'react-icons/md';
import { FaUtensils, FaRegSadTear } from 'react-icons/fa';
import { useHotelTitle } from '../hooks/useHotelTitle';

const GuestFood: React.FC = () => {
  const { hotelId, roomId } = useToken()!;
  const { hotelName } = useHotelTitle('Food & Beverages');
  const [categories, setCategories] = useState<any[]>([]);
  const [itemsByCat, setItemsByCat] = useState<{ [catId: string]: any[] }>({});
  const [selectedCat, setSelectedCat] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCart, setShowCart] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  const guest = useGuest();
  const [showCheckout, setShowCheckout] = useState(false);
  const [search, setSearch] = useState('');
  // No need for name, phone, or room fields
  const [placingOrder, setPlacingOrder] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [prepaidRequired, setPrepaidRequired] = useState(false);
  const [paymentsConfigLoaded, setPaymentsConfigLoaded] = useState(false);

  useEffect(() => {
    if (!hotelId) return;
    fetch(API_ENDPOINTS.GUEST_RAZORPAY_CONFIG(hotelId))
      .then((r) => r.json())
      .then((d) => setPrepaidRequired(!!d.fnb_prepaid_required))
      .catch(() => setPrepaidRequired(false))
      .finally(() => setPaymentsConfigLoaded(true));
  }, [hotelId]);

  useEffect(() => {
    if (!hotelId) return;
    setLoading(true);
    setError('');
    fetch(API_ENDPOINTS.ROOM_GUEST_MENU(hotelId))
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch menu');
        return res.json();
      })
      .then((data) => {
        if (!Array.isArray(data) || data.length === 0) {
          setCategories([]);
          setItemsByCat({});
          setSelectedCat('');
          setLoading(false);
          return;
        }
        setCategories(data.map(cat => ({ id: cat.category_id, name: cat.category_name })));
        const byCat: { [catId: string]: any[] } = {};
        data.forEach(cat => {
          // Always create a new array to avoid mutation
          byCat[cat.category_id] = Array.isArray(cat.items) ? [...cat.items] : [];
        });
        setItemsByCat(byCat);
        setSelectedCat(data[0].category_id);
        setLoading(false);
      })
      .catch((err) => {
        setError('Failed to load menu. Please try again.');
        setLoading(false);
      });
  }, [hotelId]);

  const { cart, addToCart, updateQuantity, clearCart, removeFromCart } = useCart();

  // Helper to get cart quantity for an item
  const getCartQty = (id: string|number) => {
    const found = cart.find(i => i.id === id);
    return found ? found.quantity : 0;
  };

  const items = selectedCat && itemsByCat[selectedCat] ? itemsByCat[selectedCat] : [];
  // Filter out duplicate items by id
  let uniqueItems = Array.from(new Map(items.map(i => [i.id, i])).values());
  // Filter by search
  if (search.trim()) {
    const q = search.trim().toLowerCase();
    uniqueItems = uniqueItems.filter(i =>
      (i.name && i.name.toLowerCase().includes(q)) ||
      (i.description && i.description.toLowerCase().includes(q))
    );
  }

  // Close dropdown on outside click
  useEffect(() => {
    if (!showCategoryDropdown) return;
    function handleClick(e: MouseEvent) {
      const dropdown = document.querySelector('.category-dropdown');
      const fab = document.querySelector('.category-fab');
      if (dropdown && !dropdown.contains(e.target as Node) && fab && !fab.contains(e.target as Node)) {
        setShowCategoryDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showCategoryDropdown]);

  const loadRazorpayScript = (): Promise<void> =>
    new Promise((resolve, reject) => {
      if (typeof window !== 'undefined' && window.Razorpay) {
        resolve();
        return;
      }
      const existing = document.getElementById('razorpay-checkout-js');
      if (existing) {
        const t = window.setInterval(() => {
          if (window.Razorpay) {
            window.clearInterval(t);
            resolve();
          }
        }, 50);
        window.setTimeout(() => {
          window.clearInterval(t);
          if (!window.Razorpay) reject(new Error('Razorpay load timeout'));
        }, 15000);
        return;
      }
      const s = document.createElement('script');
      s.id = 'razorpay-checkout-js';
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Could not load payment script'));
      document.body.appendChild(s);
    });

  const submitGuestOrder = async (
    itemsPayload: { id: string | number; qty: number }[],
    razorpay?: { razorpay_payment_id: string; razorpay_order_id: string; razorpay_signature: string }
  ) => {
    const res = await fetch(API_ENDPOINTS.GUEST_ORDER(hotelId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        hotel_id: hotelId,
        room_id: roomId,
        guest_id: guest?.guest_id,
        items: itemsPayload,
        ...(razorpay || {}),
      }),
    });
    const data = await res.json();
    if (!res.ok || data.status !== 'success') {
      const msg =
        data.message ||
        (data.messages && data.messages.error) ||
        (Array.isArray(data.messages) ? data.messages.join(' ') : '') ||
        'Failed to place order.';
      throw new Error(msg);
    }
    setCheckoutSuccess(true);
    clearCart();
  };

  return (
    <div className="content" style={{ maxWidth: 600, margin: '0 auto', padding: 12, marginTop: 80 }}>
      <h2 className="section-title mb-3" style={{ fontSize: 19, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 22, color: '#4a6cf7' }}><i className="fas fa-utensils"></i></span>
        Food & Beverages
      </h2>
      {loading ? (
        <div className="text-center my-4">
          <span className="spinner-border text-primary"></span>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : categories.length === 0 ? (
        <div className="alert alert-info">No menu items found.</div>
      ) : (
        <>
          {/* Floating Category Button (centered above tab bar) */}
          {!showCart && (
            <>
              <button
                className="category-fab"
                onClick={() => {
                  setShowCategoryDropdown(v => !v);
                  setShowCart(false); // Close cart if open
                }}
                aria-label="Show categories"
                disabled={showCart}
              >
                <MdRestaurantMenu size={28} />
                <span className="category-fab-label">{categories.find(c => c.id === selectedCat)?.name || 'Categories'}</span>
              </button>
              {showCategoryDropdown && (
                <div className="category-dropdown" tabIndex={-1}>
                  <div className="category-dropdown-list">
                    {categories.map(cat => (
                      <button
                        key={cat.id}
                        className={`category-dropdown-item${selectedCat === cat.id ? ' active' : ''}`}
                        onClick={() => {
                          setSelectedCat(cat.id);
                          setShowCategoryDropdown(false);
                        }}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          {/* Search Bar */}
          <div className="food-search-bar">
            <span style={{ color: '#bbb', fontSize: '1.15rem', marginRight: 6 }}><MdSearch size={18} /></span>
            <input
              type="text"
              className="food-search-input"
              placeholder="Search food items..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div id="food-items" style={{ paddingBottom: 80 }}>
            {uniqueItems.length === 0 ? (
              <div className="no-food-items-message">
                <span className="no-food-icon"><FaRegSadTear /></span>
                <div className="no-food-title">No items found</div>
                <div className="no-food-desc">There are no food or beverage items in this category. Please try another category or search again.</div>
              </div>
            ) : (
              <div className="food-items-grid">
                {uniqueItems.map(item => {
                  const qty = getCartQty(item.id);
                  return (
                    <div className="food-item-tile" key={item.id}>
                      <img src={item.images && item.images[0] ? (item.images[0].startsWith('http') ? item.images[0] : API_ENDPOINTS.IMAGE_BASE + item.images[0]) : '/assets/no-image.png'} className="food-item-img" alt={item.name} />
                      <div className="food-item-title">{item.name}</div>
                      {item.description && <div className="food-item-desc">{item.description}</div>}
                      {item.price && <div className="food-item-price">₹{parseFloat(item.price).toFixed(2)}</div>}
                      <div className="mt-2">
                        {item.stock == 0 ? (
                          <button className="btn btn-sm btn-secondary w-100" disabled>Out of Stock</button>
                        ) : qty === 0 ? (
                          <button className="btn btn-sm btn-primary w-100" onClick={() => addToCart({
                            id: item.id,
                            name: item.name,
                            price: parseFloat(item.price),
                            image: item.images && item.images[0]
                              ? (item.images[0].startsWith('http') ? item.images[0] : API_ENDPOINTS.IMAGE_BASE + item.images[0])
                              : undefined,
                            stock: item.stock,
                            quantity: 1
                          })}>Add to Cart</button>
                        ) : (
                          <div className="d-flex align-items-center">
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => {
                                if (qty === 1) {
                                  removeFromCart(item.id);
                                } else {
                                  updateQuantity(item.id, qty - 1);
                                }
                              }}
                              disabled={qty <= 0}
                            >-</button>
                            <span className="mx-2">{qty}</span>
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => updateQuantity(item.id, qty + 1)} disabled={qty >= item.stock}>+</button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {/* Floating Cart Button */}
          {cart.length > 0 && !showCategoryDropdown && (
            <button
              className="cart-fab btn btn-primary position-fixed"
              style={{ right: 18, bottom: 80, zIndex: 2000, borderRadius: '50%', width: 60, height: 60, boxShadow: '0 4px 16px rgba(44,62,80,0.18)' }}
              onClick={() => {
                if (!showCategoryDropdown) setShowCart(true);
                setShowCategoryDropdown(false); // Close category modal if open
              }}
              disabled={showCategoryDropdown}
            >
              <i className="fas fa-shopping-cart"></i>
              <span className="cart-fab-badge badge bg-danger" style={{ position: 'absolute', top: 8, right: 8 }}>{cart.reduce((sum, i) => sum + i.quantity, 0)}</span>
            </button>
          )}
          {/* Cart Modal */}
          <Modal show={showCart} onHide={() => setShowCart(false)} centered dialogClassName="cart-preview-modal">
            <Modal.Header closeButton>
              <Modal.Title><i className="fas fa-shopping-cart me-2"></i> Your Cart</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {cart.length === 0 ? (
                <div className="text-center my-4">Your cart is empty.</div>
              ) : (
                <div>
                  {cart.map(item => (
                    <div className="cart-item-row" key={item.id}>
                      <img src={item.image ? (item.image.startsWith('http') ? item.image : API_ENDPOINTS.IMAGE_BASE + item.image) : '/assets/no-image.png'} alt={item.name} className="cart-item-img" />
                      <div style={{ flex: 1 }}>
                        <div className="cart-item-title">{item.name}</div>
                        <div className="cart-item-price">₹{item.price.toFixed(2)}<span className="cart-item-qty">x{item.quantity}</span></div>
                      </div>
                      <div className="cart-item-total">₹{(item.price * item.quantity).toFixed(2)}</div>
                    </div>
                  ))}
                  <hr style={{ margin: '8px 0' }} />
                  <div className="cart-total-row">
                    <span>Total</span>
                    <span>₹{cart.reduce((sum, i) => sum + i.price * i.quantity, 0).toFixed(2)}</span>
                  </div>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="success" className="btn-sm" disabled={cart.length === 0} onClick={() => { setShowCart(false); setShowCheckout(true); }}>
                Checkout
              </Button>
            </Modal.Footer>
          </Modal>
         {/* Checkout Modal */}
         <Modal show={showCheckout} onHide={() => { setShowCheckout(false); setCheckoutError(''); setCheckoutSuccess(false); }} centered dialogClassName="checkout-modal">
           <Modal.Header closeButton>
             <Modal.Title>Checkout</Modal.Title>
           </Modal.Header>
           <Modal.Body>
             {checkoutSuccess ? (
               <div className="text-center my-4">
                 <i className="fas fa-check-circle fa-3x text-success mb-3"></i>
                 <div className="fs-5 mb-2">Order placed successfully!</div>
                 <div className="mb-2">Our staff will contact you soon.</div>
               </div>
             ) : (
               <form
                 onSubmit={async (e) => {
                   e.preventDefault();
                   setCheckoutError('');
                   setPlacingOrder(true);
                   const itemsPayload = cart.map((i) => ({ id: i.id, qty: i.quantity }));
                   try {
                     if (prepaidRequired) {
                       const cr = await fetch(API_ENDPOINTS.GUEST_RAZORPAY_CREATE_ORDER(hotelId), {
                         method: 'POST',
                         headers: { 'Content-Type': 'application/json' },
                         body: JSON.stringify({
                           room_id: roomId,
                           items: itemsPayload,
                         }),
                       });
                       const cd = await cr.json();
                       if (cr.status === 503 || cd.status === 'disabled') {
                         setCheckoutError(
                           cd.message || 'Online payment is not available. Please contact the front desk.'
                         );
                         setPlacingOrder(false);
                         return;
                       }
                       if (!cr.ok || cd.status !== 'success' || !cd.key_id || !cd.razorpay_order_id) {
                         setCheckoutError(
                           cd.message || (cd.messages && cd.messages.error) || 'Could not start payment.'
                         );
                         setPlacingOrder(false);
                         return;
                       }
                       await loadRazorpayScript();
                       if (!window.Razorpay) {
                         setCheckoutError('Payment could not be initialized.');
                         setPlacingOrder(false);
                         return;
                       }
                       const rzp = new window.Razorpay({
                         key: cd.key_id,
                         amount: cd.amount,
                         currency: cd.currency || 'INR',
                         order_id: cd.razorpay_order_id,
                         name: hotelName || 'QRoom',
                         description: 'Food & beverage order (prepaid)',
                         handler: async (response) => {
                           setPlacingOrder(true);
                           setCheckoutError('');
                           try {
                             await submitGuestOrder(itemsPayload, {
                               razorpay_payment_id: response.razorpay_payment_id,
                               razorpay_order_id: response.razorpay_order_id,
                               razorpay_signature: response.razorpay_signature,
                             });
                           } catch (err: unknown) {
                             setCheckoutError(err instanceof Error ? err.message : 'Order failed after payment.');
                           } finally {
                             setPlacingOrder(false);
                           }
                         },
                         modal: {
                           ondismiss: () => setPlacingOrder(false),
                         },
                         theme: { color: '#4f46e5' },
                       });
                       rzp.open();
                       return;
                     }
                     await submitGuestOrder(itemsPayload);
                     setPlacingOrder(false);
                   } catch (err: unknown) {
                     setCheckoutError(err instanceof Error ? err.message : 'Network error.');
                     setPlacingOrder(false);
                   }
                 }}
               >
                 <div className="mb-3">
                   <label className="form-label">Order Summary</label>
                   {prepaidRequired && paymentsConfigLoaded && (
                     <p className="small text-muted mb-2">
                       Orders are prepaid only. You will complete payment with Razorpay before the order is sent to the
                       kitchen.
                     </p>
                   )}
                   <ul className="list-group mb-2">
                     {cart.map((i) => (
                       <li className="list-group-item d-flex justify-content-between align-items-center" key={i.id}>
                         <span>
                           {i.name} x{i.quantity}
                         </span>
                         <span>₹{(i.price * i.quantity).toFixed(2)}</span>
                       </li>
                     ))}
                   </ul>
                   <div className="d-flex justify-content-between fw-bold">
                     <span>Total</span>
                     <span>₹{cart.reduce((sum, i) => sum + i.price * i.quantity, 0).toFixed(2)}</span>
                   </div>
                 </div>
                 {checkoutError && <div className="alert alert-danger">{checkoutError}</div>}
                 <Button
                   variant="success"
                   type="submit"
                   className="w-100 btn-sm"
                   disabled={placingOrder || !paymentsConfigLoaded}
                 >
                   {placingOrder
                     ? prepaidRequired
                       ? 'Processing...'
                       : 'Placing Order...'
                     : prepaidRequired
                       ? 'Pay with Razorpay'
                       : 'Place Order'}
                 </Button>
               </form>
             )}
           </Modal.Body>
         </Modal>
        </>
      )}
    </div>
  );
};

export default GuestFood; 